
import Header from "common/js/header/header";
import { ToastContainer } from "react-toastify";
import React, { useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import { getLiveVehicleListSvc } from "sms/transport/js/vehicleSvc";
import { CustomInfoWindow } from './CustomInfoWindow'
const lerp = (start, end, t) => start + (end - start) * t;
const DEFAULT_CENTER = { lat: 12.568338, lng: 78.591072 };


const BUS_COLORS = [
  "#e53935", "#1565c0", "#2e7d32", "#f9a825", "#6a1b9a", "#00838f", "#d84315", "#c2185b",
  "#3949ab", "#00897b", "#7cb342", "#fb8c00", "#8e24aa", "#039be5", "#c0ca33", "#5d4037",
  "#d81b60", "#43a047", "#fdd835", "#1e88e5", "#f4511e", "#00acc1", "#9c27b0", "#546e7a",
];
const colorForId = (id) => {
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return BUS_COLORS[h % BUS_COLORS.length];
};
// bus shape ka SVG, diye gaye color ke saath (data-URI image)
const busSvg = (color) =>
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="${color}"><path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4S4 2.5 4 6v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z"/></svg>`
  );
const getBearing = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;
  const dLng = toRad(lng2 - lng1);
  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

function LiveVehicleMap() {
  const [loader, setLoader] = useState("none");
  const [vehicles, setVehicles] = useState([]);
  const [hoveredVehicle, setHoveredVehicle] = useState(null);

  const targetVehiclesRef = useRef([]);
  const animationFrameRef = useRef(null);
  const mapRef = useRef(null);
  const initialLoadRef = useRef(true);

  // ---- Store path history per vehicle: { vehicleId: [{lat, lng}, ...] } ----
  const pathHistoryRef = useRef({});
  const [paths, setPaths] = useState({});
  const [shouldFitMap, setShouldFitMap] = useState(false);
  const [mapReady, setMapReady] = useState(false); // map onLoad ho gaya?

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyC_G16AeOUmhNAP9FuTvreRhFTs31bL4mQ",
  });

  const fetchLiveVehicles = async () => {
    try {
      if (initialLoadRef.current) {
        setLoader("block");
      }

      const data = await getLiveVehicleListSvc();
      console.log("Live vehicle backend response:", data);

      if (data?.SvcStatus === "Success") {
        const liveLocations = data.lstLocation || [];

        console.log("Live vehicle locations used for map:", liveLocations);

        const newVehicles = liveLocations
          .map((v) => ({
            ...v,
            lat: Number(v.lat),
            lng: Number(v.lng),
          }))
          .filter((v) => Number.isFinite(v.lat) && Number.isFinite(v.lng));

        const updated = newVehicles.map((newV) => {
          const old = targetVehiclesRef.current.find(
            (v) => v.vehicleId === newV.vehicleId
          );
          const bearing = old
            ? getBearing(old.lat, old.lng, newV.lat, newV.lng)
            : 0;
          return {
            ...newV,
            bearing,
            prevLat: old?.lat ?? newV.lat,
            prevLng: old?.lng ?? newV.lng,
          };
        });

        targetVehiclesRef.current = updated;

        // ---- Update path history for each vehicle ----
        updated.forEach((v) => {
          console.log("Live vehicle point received:", {
            vehicleId: v.vehicleId,
            regsNo: v.regsNo,
            lat: v.lat,
            lng: v.lng,
            validPoint: Number.isFinite(v.lat) && Number.isFinite(v.lng),
          });

          if (!pathHistoryRef.current[v.vehicleId]) {
            pathHistoryRef.current[v.vehicleId] = [];
          }

          const history = pathHistoryRef.current[v.vehicleId];
          const lastPoint = history[history.length - 1];
          const nextPoint = { lat: v.lat, lng: v.lng };

          pathHistoryRef.current[v.vehicleId] = [...history, nextPoint];
          console.log("history.push point:", {
            vehicleId: v.vehicleId,
            regsNo: v.regsNo,
            previousPoint: lastPoint || null,
            pushedPoint: nextPoint,
            historyLength: pathHistoryRef.current[v.vehicleId].length,
            history: pathHistoryRef.current[v.vehicleId],
          });

        });

        setPaths({ ...pathHistoryRef.current });
        if (initialLoadRef.current) {
          setShouldFitMap(true);
        }

        animateVehicles();
      }
    } catch (err) {
      console.log("Live vehicle API error:", err);
    } finally {
      if (initialLoadRef.current) {
        setLoader("none");
        initialLoadRef.current = false;
      }
    }
  };

  const animateVehicles = () => {
    const duration = 800;
    const startTime = performance.now();

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const step = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);

      const interpolated = targetVehiclesRef.current.map((v) => ({
        ...v,
        lat: lerp(v.prevLat, v.lat, t),
        lng: lerp(v.prevLng, v.lng, t),
      }));

      setVehicles(interpolated);

      if (t < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      }
    };

    animationFrameRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    fetchLiveVehicles();

    const interval = setInterval(() => {
      fetchLiveVehicles();
    }, 65000);

    return () => {
      clearInterval(interval);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldFitMap || !isLoaded || !mapReady || !mapRef.current) return;

    const allPoints = Object.values(paths).flat();
    if (!allPoints.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    allPoints.forEach((point) => bounds.extend(point));
    mapRef.current.fitBounds(bounds);
    setShouldFitMap(false);
  }, [shouldFitMap, isLoaded, mapReady, paths]);

  if (!isLoaded) return <h3>Loading Map...</h3>;

  return (
    <div
      className="MainContainer"
      style={{ pointerEvents: loader === "none" ? "auto" : "none" }}
    >
      <style>
        {`
          .gm-ui-hover-effect {
            width: 16px !important;
            height: 16px !important;
            opacity: 0.7;
          }
          .gm-ui-hover-effect img {
            width: 12px !important;
            height: 12px !important;
            margin: 0 !important;
          }
          .gm-ui-hover-effect:hover { opacity: 1; }
          .gm-style-iw { padding: 0 !important; }
          .gm-style-iw-d { overflow: hidden !important; }
        `}
      </style>

      <div
        style={{
          position: "fixed",
          zIndex: 10000,
          width: 100,
          height: 100,
          marginLeft: -50,
          marginTop: -50,
          left: "50%",
          top: "50%",
          display: loader,
        }}
      >
        <img className="loderImg" src={require("common/images/loader.gif")} alt="Loader..." />
      </div>

      <Header />
      <ToastContainer autoClose={3000} hideProgressBar />

      <div style={{ height: "calc(100vh - 70px)", width: "100%" }}>
        <GoogleMap
          mapContainerStyle={{ height: "100%", width: "100%" }}
          center={DEFAULT_CENTER}
          zoom={40}
          onLoad={(map) => {
            mapRef.current = map;
            setMapReady(true);
          }}
        >
          {/* ---- Draw path/trail for each vehicle ---- */}
          {Object.entries(paths).map(([vehicleId, points]) =>
            points.length > 1 ? (
              <Polyline
                key={`path-${vehicleId}`}
                path={points}
                options={{
                  strokeColor: colorForId(vehicleId),
                  strokeOpacity: 1,
                  strokeWeight: 6,
                  geodesic: true,
                }}
              />
            ) : null
          )}

          {vehicles.map((vehicle) => (
            <Marker
              key={vehicle.vehicleId}
              position={{ lat: vehicle.lat, lng: vehicle.lng }}
              icon={{
                url: busSvg(colorForId(vehicle.vehicleId)),
                scaledSize: new window.google.maps.Size(32, 32),
                anchor: new window.google.maps.Point(16, 16),
                rotation: vehicle.bearing || 0,
              }}
              onMouseOver={() => setHoveredVehicle(vehicle)}
              onClick={() => setHoveredVehicle(vehicle)}
            />
          ))}

          {hoveredVehicle && (
            <CustomInfoWindow
              position={{ lat: hoveredVehicle.lat, lng: hoveredVehicle.lng }}
              onClose={() => setHoveredVehicle(null)}
            >
              <div style={{ minWidth: "100px", padding: "2px", fontFamily: "Arial, sans-serif" }}>
                <h4 style={{ margin: "0px", fontSize: "13px", fontWeight: "bold", color: "#202124" }}>
                  {hoveredVehicle.regsNo}
                </h4>
                <p style={{ margin: 0, fontSize: "11px", color: "#5f6368" }}>
                  Last Time: {hoveredVehicle.lastDttm}
                </p>
              </div>
            </CustomInfoWindow>
          )}
        </GoogleMap>
      </div>
    </div>
  );
}

export default LiveVehicleMap;